/**
 * StorageCleanup.jsx — v2 widget for chart cache disk usage + manual
 * cleanup (issue #248). Ports the v1 admin storage panel.
 *
 * Read path: /api/v2/widgets/storage-cleanup (admin-tier).
 * Write path: POST /api/admin/storage/cleanup — kept on the v1 admin
 * route so we don't duplicate chart-service plumbing. The endpoint is
 * already admin-gated by the /api/admin middleware and rate-limited.
 *
 * CSP: handlers attach via Solid's onClick (no inline event attrs).
 */

import { Show, For, createMemo, createSignal } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { qty, relTime } from '../lib/format.js';
import toast from '../lib/toastStore.js';
import wm from '../lib/wmStore.js';

function pct(n) {
  if (n == null) return '—';
  return Number(n).toFixed(1) + '%';
}

export function StorageCleanupPanel(props) {
  const [showFiles, setShowFiles] = createSignal(!!props.settings?.showFiles);
  const fileLimit = () => Math.min(Math.max(props.settings?.fileLimit || 25, 5), 200);

  function toggleShowFiles() {
    const next = !showFiles();
    setShowFiles(next);
    const wid = props.widget?.id;
    if (wid) {
      wm.setSettings(wid, { ...(props.widget?.settings || {}), showFiles: next });
    }
  }

  const { data, loading, error, refetch } = useWidgetData('storage-cleanup',
    () => ({ showFiles: showFiles(), fileLimit: fileLimit() }));

  const [busy, setBusy]     = createSignal(false);
  const [lastResult, setLastResult] = createSignal(null);

  const usagePct = createMemo(() => Number(data()?.usagePercent || 0));
  const files    = createMemo(() => data()?.files || []);

  async function runCleanup() {
    if (busy()) return;
    setBusy(true);
    setLastResult(null);
    try {
      const res = await fetch('/api/admin/storage/cleanup', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.ok === false) {
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      setLastResult(body);
      // Refresh the widget data so totals update.
      refetch();
    } catch (err) {
      toast.error('Cleanup failed', { detail: err?.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class={`v2-system-panel v2-system-panel--cleanup ${props.class || ''}`}>
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__title">Storage / Cleanup</span>
        <button class="v2-widget-hdr__btn" style={{ 'margin-left': 'auto' }}
          title="Refresh" onClick={() => refetch()}>↻</button>
      </div>
      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class="v2-widget__scroll">

            <div class="v2-perf-section">
              <div class="v2-perf-section-label">Chart cache</div>
              <div class="v2-stat-grid">
                <div class="v2-stat">
                  <span class="v2-stat__label">Used</span>
                  <span class="v2-stat__value">{(data()?.totalMB ?? 0).toFixed(2)} MB</span>
                </div>
                <div class="v2-stat">
                  <span class="v2-stat__label">Cap</span>
                  <span class="v2-stat__value">{qty(data()?.maxMB ?? 0)} MB</span>
                </div>
                <div class="v2-stat v2-stat--cyan">
                  <span class="v2-stat__label">Files</span>
                  <span class="v2-stat__value">{qty(data()?.fileCount ?? 0)}</span>
                </div>
                <div class="v2-stat"
                  style={{ 'border-left-color': usagePct() > 90 ? 'var(--bad)'
                          : usagePct() > 70 ? 'var(--gold)' : 'var(--t-green)' }}>
                  <span class="v2-stat__label">Usage</span>
                  <span class="v2-stat__value"
                    style={{ color: usagePct() > 90 ? 'var(--bad)'
                            : usagePct() > 70 ? 'var(--gold)' : 'inherit' }}>
                    {pct(usagePct())}
                  </span>
                </div>
              </div>
              {/* Usage bar */}
              <div style={{
                height: '8px',
                margin: '8px 0',
                background: 'rgba(255,255,255,0.06)',
                'border-radius': '4px',
                overflow: 'hidden',
              }}>
                <div style={{
                  width: Math.min(100, usagePct()) + '%',
                  height: '100%',
                  background: usagePct() > 90 ? 'var(--bad)'
                            : usagePct() > 70 ? 'var(--gold)' : 'var(--t-green)',
                  transition: 'width 200ms ease',
                }} />
              </div>

              <div style={{ display: 'flex', gap: '8px', 'align-items': 'center', 'flex-wrap': 'wrap' }}>
                <button
                  class="v2-perf-filter-chip is-on"
                  disabled={busy()}
                  onClick={runCleanup}
                  title="Force cleanup of oldest charts down to the configured cap"
                >{busy() ? 'Cleaning…' : '🧹 Run cleanup'}</button>
                <button
                  type="button"
                  class={`v2-perf-filter-chip ${showFiles() ? 'is-on' : ''}`}
                  onClick={toggleShowFiles}
                  title="Toggle oldest chart files table"
                >📄 {showFiles() ? 'Hide files' : 'Show oldest files'}</button>
                <Show when={lastResult()}>
                  <span style={{ 'font-size': '11px', color: 'var(--t-green-dim)' }}>
                    Removed {qty(lastResult().deleted ?? 0)} file(s) ·
                    {' '}{qty(lastResult().remainingMB ?? 0)} MB remain
                  </span>
                </Show>
              </div>
            </div>

            <Show when={showFiles() && files().length > 0}>
              <div class="v2-perf-section">
                <div class="v2-perf-section-label">
                  Oldest {qty(files().length)} file(s)
                </div>
                <table class="v2-perf-table">
                  <thead><tr>
                    <th style={{ 'text-align': 'left' }}>Name</th>
                    <th>Size</th><th>Created</th>
                  </tr></thead>
                  <tbody>
                    <For each={files()}>{(f) => (
                      <tr>
                        <td class="v2-perf-route" title={f.name}>{f.name}</td>
                        <td>{f.sizeMB?.toFixed(3)} MB</td>
                        <td style={{ 'font-size': '9px', color: 'var(--t-green-dim)' }}>
                          {f.createdAt ? relTime(f.createdAt) : '—'}
                        </td>
                      </tr>
                    )}</For>
                  </tbody>
                </table>
              </div>
            </Show>

          </div>
        </Show>
      </Show>
    </div>
  );
}

export default function StorageCleanup(props) {
  return <StorageCleanupPanel {...props} class="v2-widget" />;
}
